import { isExecutedOrder, Order, OrderTrade } from './order.model';
import { OrderActionEnum, OrderResponseDto } from './order-response.model';
import { Symbol, symbolIcon } from './symbol.model';
import { TradeResponseDto } from './trade-response.model';

/**
 * Maps the `/orders` API response to the presentation model consumed by the order
 * cards/detail sheet. Everything the sheet shows comes from `/orders` itself — the orders
 * list makes no symbol lookup — so fields the endpoint doesn't carry are filled in with
 * empty defaults rather than guessed values.
 *
 * `/orders` reports `symbol` as the raw exchange code (`"CD1SIB0001"`), which names no
 * commodity — so `symbolIcon` lands on its own default for as long as that stays true. The
 * short name is the endpoint's own `symbolShortName`, not that code: substituting the code
 * would put a Latin exchange code where the card expects a Persian name.
 */
export function mapOrderResponseToOrder(dto: OrderResponseDto): Order {
	return {
		...dto,
		symbolIcon: symbolIcon(dto.symbolIsin ?? ''),
		symbolShortName: dto.symbolShortName ?? '',
		// Read off the state rather than the volume: only a filled order counts, so an order
		// that was cancelled or rejected before filling is not "executed" despite being over.
		isExecuted: isExecutedOrder(dto.state),
		isEdited: dto.action === OrderActionEnum.Modify,
		// Per-fill breakdown comes from `/trades/{orderId}`, which is only fetched for
		// the order whose detail sheet is open — until then the sheet's trade section
		// stays hidden (it already guards on an empty list).
		trades: [],
		// Commission is the symbol's, not the order's, and the symbol snapshot is likewise only
		// called for the order being opened. Zero until then, which leaves a fill's value
		// at its bare price x quantity rather than at a guessed rate.
		buyCommissionRate: 0,
		sellCommissionRate: 0
	};
}

/** The only part of a symbol snapshot entry «ارزش معامله» needs. */
export type CommissionRates = Pick<Symbol, 'buyCommission' | 'sellCommission'>;

/**
 * Puts the symbol's per-side commission rates on the order, so «ارزش معامله» can report
 * what each fill actually cost or returned.
 *
 * A missing symbol leaves the rates alone rather than zeroing them: the symbol snapshot failing
 * under an open sheet should not quietly restate the values it already showed.
 */
export function withCommissionRates(order: Order, symbol: CommissionRates | undefined): Order {
	if (!symbol) {
		return order;
	}

	return {
		...order,
		buyCommissionRate: symbol.buyCommission,
		sellCommissionRate: symbol.sellCommission
	};
}

/**
 * Folds a `/trades/{orderId}` response into the order it belongs to, producing the
 * per-fill breakdown the detail sheet renders under «ریز معاملات».
 *
 * Busted fills are dropped: the exchange keeps them in the response but they no longer
 * count towards the order, so showing them would make the parts add up to more than the
 * executed volume. The rest are ordered oldest-first, which is what lets the sheet lay
 * them out as consecutive segments of one 0-100% track.
 */
export function withTrades(order: Order, trades: readonly TradeResponseDto[]): Order {
	return {
		...order,
		trades: trades
			.filter(trade => !trade.isCancelled)
			.map(mapTradeResponseToOrderTrade)
			.sort((first, second) => Date.parse(first.executedAt) - Date.parse(second.executedAt))
	};
}

/** `tradeId` is numeric, but the list is keyed by a string id like every other trade source. */
export function mapTradeResponseToOrderTrade(dto: TradeResponseDto): OrderTrade {
	return {
		id: String(dto.tradeId),
		quantity: dto.quantity,
		price: dto.price,
		executedAt: dto.tradeDate
	};
}

/**
 * Carries the fields that were layered on top of a previous `/orders` snapshot — the
 * fills from `/trades`, and whatever display fields the new snapshot came back without —
 * over onto a freshly fetched copy of the same order.
 *
 * Without this the breakdown would blank whenever the list reloads underneath an open
 * detail sheet — pulling the header's refresh, say — and only come back on the next
 * `/trades` call, which nothing would trigger.
 */
export function withPreviousDisplayFields(order: Order, previous: Order | undefined): Order {
	if (!previous) {
		return order;
	}

	return {
		...order,
		symbolIcon: previous.symbolIcon,
		symbolName: order.symbolName ?? previous.symbolName,
		symbolShortName: order.symbolShortName || previous.symbolShortName,
		trades: previous.trades,
		buyCommissionRate: previous.buyCommissionRate,
		sellCommissionRate: previous.sellCommissionRate
	};
}
